import { pipe } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import extensionsReducer from '@app/reducers/entities/extensions';
import { getAccountFromSessionSelector } from '@app/reducers/session';

import { checkAcl } from '@flow/libs/utils/acl';
import { ExtensionLogo } from '@app/components/extension/extension-logo';
import * as Command from '@app/components/filterable';
import { categorize } from '@app/components/extension/extension-category/extension-category';
import CommandItem from '../item';
import style from './search-marketplace.module.scss';
import { ACLs } from '../constants';

type Props = {
  closeCmdk: () => void;
};

export const SearchMarketplace = ({ closeCmdk }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const account = useSelector(getAccountFromSessionSelector) as Oneflow.Account;

  // biome-ignore lint/correctness/useExhaustiveDependencies: The effect needs to be memoized
  useEffect(() => {
    dispatch(
      extensionsReducer.queryExtensions({
        name: 'find-in-marketplace',
      }),
    );
  }, []);

  const query = useSelector((state) =>
    extensionsReducer.getQuerySelector(state, {
      name: 'find-in-marketplace',
    }),
  );

  const extensions = useSelector((state) =>
    extensionsReducer.getExtensionsSelector(state, {
      ids: query.result,
    }),
  );

  const categorizedExtensions = useMemo(
    () => categorize(extensions as Oneflow.Extension[]),
    [
      extensions,
    ],
  );

  const navigateTo = (path: string) => {
    if (history.location.pathname !== path) {
      history.push(path);
    }
  };

  if (!checkAcl(account.acl, ACLs.page.FIND_IN_MARKETPLACE)) {
    return null;
  }

  return (
    <>
      {categorizedExtensions.map((categoryData) => (
        <Command.Group
          key={categoryData.category}
          id={`find-in-marketplace-${categoryData.category}`}
          heading={categoryData.title}
          headingId={`find-in-marketplace-${categoryData.category}`}
        >
          {categoryData.extensions.map((extension) => (
            <CommandItem
              key={extension.id}
              id={`find-in-marketplace-${extension.id}`}
              keywords={[
                categoryData.title,
              ]}
              value={extension.name}
              onSelect={pipe(closeCmdk, () => {
                navigateTo(`/marketplace/${extension.slug}`);
              })}
            >
              <ExtensionLogo
                extension={extension}
                className={style.Logo}
              />
              {extension.name}
            </CommandItem>
          ))}
        </Command.Group>
      ))}
    </>
  );
};
